/*
 * Qenova PDP interactions
 * - Sticky tab nav: smooth-scroll on click, active-state by anchor visibility
 * - Product gallery: thumb-click swaps main image (desktop + mobile rails)
 * - Sticky mobile ATC: reveal once buy-box scrolls out of view, hide when in checkout flow
 */
(function () {
  'use strict';

  // ============================================================
  // 1) Sticky tab nav — smooth-scroll on click, active-state on scroll
  // ============================================================
  const tabContainer = document.querySelector('[data-qpdp-tabs]');
  if (tabContainer) {
    const tabs = tabContainer.querySelectorAll('[data-qpdp-tab]');
    const tabsById = new Map();
    const sections = [];

    tabs.forEach((tab) => {
      const targetId = tab.getAttribute('data-qpdp-tab');
      tabsById.set(targetId, tab);
      const section = document.getElementById(targetId);
      if (section) sections.push({ id: targetId, el: section });

      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const el = document.getElementById(targetId);
        if (!el) return;
        // Offset for fixed pill header + sticky tabs themselves
        const offsetTop = el.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      });
    });

    // Active-state detection via IntersectionObserver
    if (sections.length) {
      const setActive = (id) => {
        tabs.forEach((t) => t.classList.toggle('is-active', t.getAttribute('data-qpdp-tab') === id));
      };

      const io = new IntersectionObserver(
        (entries) => {
          // Pick the first entry whose top is within the viewport top half
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length) setActive(visible[0].target.id);
        },
        {
          rootMargin: '-120px 0px -55% 0px',
          threshold: 0.01,
        }
      );

      sections.forEach((s) => io.observe(s.el));
    }
  }

  // ============================================================
  // 2) Product gallery — thumb swap
  // ============================================================
  const gallery = document.querySelector('[data-qpgal]');
  if (gallery) {
    const main = gallery.querySelector('[data-qpgal-main]');
    const thumbs = gallery.querySelectorAll('[data-qpgal-thumb]');
    if (main && thumbs.length) {
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.getAttribute('data-thumb-src');
          const srcset = thumb.getAttribute('data-thumb-srcset');
          const alt = thumb.getAttribute('data-thumb-alt');
          if (src) main.src = src;
          if (srcset) main.srcset = srcset;
          if (alt) main.alt = alt;
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    }

    // Variant change → swap main image and active thumb to variant's featured_media.
    // orreno-buy-box (components.js) dispatches `orreno:variant` on every option change,
    // but only swaps URL via replaceState — no reload. Without this, the gallery stays put.
    const mediaMapEl = gallery.querySelector('[data-qpgal-variant-media]');
    if (main && mediaMapEl) {
      let mediaMap = {};
      try { mediaMap = JSON.parse(mediaMapEl.textContent || '{}'); } catch (e) {}
      document.addEventListener('orreno:variant', (e) => {
        const v = e.detail && e.detail.variant;
        const entry = v && mediaMap[v.id];
        if (!entry) return;
        main.src = entry.src;
        if (entry.srcset) main.srcset = entry.srcset; else main.removeAttribute('srcset');
        if (entry.alt) main.alt = entry.alt;
        const mid = String(entry.mediaId);
        thumbs.forEach((t) => {
          t.classList.toggle('is-active', t.getAttribute('data-thumb-media-id') === mid);
        });
      });
    }
  }

  // ============================================================
  // 3) Sticky mobile ATC — reveal after scrolling past buy-box
  // ============================================================
  const stickyAtc = document.querySelector('[data-qsatc]');
  if (stickyAtc) {
    const buyBox = document.querySelector('.qbb');
    if (buyBox && window.matchMedia('(max-width: 749px)').matches) {
      stickyAtc.hidden = false;

      const setVisible = (visible) => {
        stickyAtc.setAttribute('data-qsatc-visible', visible ? 'true' : 'false');
      };
      setVisible(false);

      const atcInBuyBox = buyBox.querySelector('[data-atc]');

      const sentinelObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show sticky when buy-box's primary ATC is OUT of view
            setVisible(!entry.isIntersecting);
          });
        },
        {
          rootMargin: '0px 0px -80px 0px',
          threshold: 0.01,
        }
      );

      if (atcInBuyBox) {
        sentinelObserver.observe(atcInBuyBox);
      } else {
        // Fallback: observe whole buy-box
        sentinelObserver.observe(buyBox);
      }

      // Click sticky ATC → click buy-box ATC (same behavior, opens cart drawer / submits form)
      const stickyBtn = stickyAtc.querySelector('[data-qsatc-atc]');
      if (stickyBtn && atcInBuyBox) {
        stickyBtn.addEventListener('click', () => {
          atcInBuyBox.click();
        });
      }
    }
  }

  // ============================================================
  // 4) Variant change → update sticky ATC price + variant title
  // ============================================================
  if (stickyAtc) {
    const priceEl = stickyAtc.querySelector('[data-qsatc-price]');
    const variantEl = stickyAtc.querySelector('[data-qsatc-variant]');
    const buyBox = document.querySelector('.qbb');
    if (buyBox && (priceEl || variantEl)) {
      const observer = new MutationObserver(() => {
        const liveTitle = buyBox.querySelector('.qbb__title');
        const livePrice = buyBox.querySelector('[data-price]');
        if (livePrice && priceEl) priceEl.textContent = livePrice.textContent.trim();
        if (liveTitle && variantEl) variantEl.textContent = liveTitle.textContent.trim();
      });
      const livePriceNode = buyBox.querySelector('[data-price]');
      if (livePriceNode) observer.observe(livePriceNode, { childList: true, characterData: true, subtree: true });
    }
  }

  // ============================================================
  // 5) Related products — request Shopify recommendations near viewport
  // ============================================================
  document.querySelectorAll('[data-qrec][data-qrec-loaded="false"]').forEach((section) => {
    const load = async () => {
      if (section.dataset.qrecLoading === 'true') return;
      section.dataset.qrecLoading = 'true';

      try {
        const response = await fetch(section.dataset.qrecUrl, {
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const freshSection = documentFragment.querySelector('[data-qrec]');
        if (!freshSection) throw new Error('Recommendations section missing from response');
        section.replaceWith(freshSection);
      } catch (error) {
        section.hidden = true;
        section.setAttribute('aria-busy', 'false');
        console.warn('[qenova-recommendations]', error);
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        load();
      }, { rootMargin: '500px 0px' });
      observer.observe(section);
    } else {
      load();
    }
  });
})();
